import React, { useRef } from "react";

import Page from "../models/Page";
import ConnectableObject from "../models/ConnectableObject";

import Connection, {
  ConnectionType,
  dehighlightConnections,
  getTypeFromDevice,
  highlightConnections,
} from "../models/Connection";
import type Device from "../models/Device";
import { ChannelType, channelTypeInfo } from "../models/ChannelType";
import Channel from "../models/Channel";
import Logger from "../Logger";
import { PagePrintInfo } from "../models/PagePrintInfo";
import AllowedConnections from "../models/AllowedConnections";
import { itx, noItx } from "../translationItx/itx";
import { showToast } from "../components/Toasts";

const logger = new Logger();

function clearCurrentDrawingConnectionTarget() {
  if (state.currentDrawingConnection.connection) {
    state.currentDrawingConnection.connection.toDevice = undefined;
    state.currentDrawingConnection.connection.tmpType = undefined;
    state.currentDrawingConnection.connection.type = ConnectionType.FromSourceDevice;

    state.currentDrawingConnection.currentTargetIsChannel = false;
  }
}
function clearCurrentDrawingConnection() {
  if (state.currentDrawingConnection.connection) {
    clearCurrentDrawingConnectionTarget();
    state.currentDrawingConnection.connection = null;
  }
}

let lastTarget: ConnectableObject | undefined;

export function onConnectionTargetMouseMove(
  page: Page,
  target: ConnectableObject,
  targetElement: HTMLDivElement,
  connectionType: ConnectionType,
) {
  function handleChannelButtonChannelConnecting() {
    const connection = state.currentDrawingConnection.connection;
    const fromDevice = connection?.fromDevice;
    let channelOrActor = target.isActorChannel || target.isActor ? target : fromDevice;
    let buttonChannelOrButton = target.className.includes("Button") ? target : fromDevice;

    if (
      (channelOrActor?.isActorChannel || channelOrActor?.isActor) &&
      buttonChannelOrButton?.className.includes("Button")
    ) {
      // redirect here

      if (channelOrActor.isActorChannel) {
        // redirect Channel to connected Consumer
        const connectedConsumer = (channelOrActor as Channel).getConnectedConsumer();
        if (connectedConsumer) {
          if (target == channelOrActor && connection) {
            target = connectedConsumer;
            if (lastTarget == target) {
              return;
            }

            if (!connection.animateFromPoint) {
              connection.animateFromPoint = connection.tmpToPoint;
              connection.animateStartTime = Date.now();
              connection.type = ConnectionType.Button;
              lastTarget = target;

              showToast({
                text: itx(
                  "ConnectionTarget.bitteverbindetasten",
                  "Bitte verbinde Tasten mit Verbauchern, \n und nicht mit Aktoren. \n Ich passe das für Dich automatisch an.",
                ),
                time: 10000,
              });
              return;
            }
          }
        }
      }
    }
    lastTarget = undefined;
  }
  logger.log("ConnectionTarget onMouseMove a");
  if (state.currentDrawingConnection.connection) {
    // connection from / to same object are forbidden
    if (target == state.currentDrawingConnection.connection.fromDevice) {
      state.currentDrawingConnection.connection.forbidden = true;
      return;
    }

    const device = target.parent as Device;
    logger.log("ConnectionTarget onMouseMove b", target.name, device?.name);

    if (connectionType == ConnectionType.FromSourceDevice || connectionType == ConnectionType.Unknown) {
      if (state.currentDrawingConnection.connection.fromDevice) {
        connectionType = getTypeFromDevice(state.currentDrawingConnection.connection.fromDevice);
      }
    }

    const oldTarget = state.currentDrawingConnection.connection.toDevice;
    if (oldTarget && oldTarget != target && oldTarget.className == "Channel") {
      (oldTarget as Channel).selectedForAutoconect = false;

      oldTarget.raiseOnChange();
    }
    // state.currentDrawingConnection.currentTargetIsChannel = true;

    var isConnectable = true;
    const connection = state.currentDrawingConnection.connection;
    const fromDevice = connection.fromDevice;

    if (fromDevice != undefined) {
      // handleChannelButtonChannelConnecting(); // irreführend
      isConnectable = AllowedConnections.isConnectionAllowed(fromDevice, target, true);

      if (isConnectable) {
        if (fromDevice.className.includes("Button") || target.className.includes("Button")) {
          const partnerClassName = fromDevice.className == "Button" ? target.className : fromDevice.className;
          switch (partnerClassName) {
            case noItx("Consumer"):
              connection.type = ConnectionType.Button;
              break;
            case noItx("Scene"):
              connection.type = ConnectionType.Scene;
              break;
            case "Multiplexer":
              connection.type = ConnectionType.Multiplexer;
              break;
            default:
              connection.type = connectionType;
              break;
          }
        } else if (fromDevice.className == "Consumer" && target.className == "Consumer") {
          if (
            (channelTypeInfo(fromDevice.channelType)?.isRollo && target.channelType == ChannelType.Lock) ||
            (fromDevice.channelType == ChannelType.Lock && channelTypeInfo(target.channelType)?.isRollo)
          ) {
            connection.type = ConnectionType.Button;
          } else {
            connection.type = ConnectionType.TerminalVia;
          }
        } else {
          connection.type = connectionType;
        }
      }
    }

    connection.forbidden = !isConnectable;
    if (isConnectable && fromDevice != undefined) {
      connection.toDevice = target;

      connection.tmpType = connectionType;
    } else {
      connection.tmpType = undefined;
      connection.toDevice = undefined;

      connection.forbidden = true;
      logger.log("connection forbidden");
    }

    connection.raiseOnChange();
    if (target.className == "Channel") {
      (target as Channel).selectedForAutoconect = true;

      target.raiseOnChange();
    }
  }
}

export function onConnectionTargetMouseUp(
  target: ConnectableObject,
  targetElement: HTMLDivElement,
  connectionType: ConnectionType,
) {
  state.currentDrawingConnection.currentTargetIsChannel = false;
  lastTarget = undefined;

  if (state.currentDrawingConnection.connection && state.currentDrawingConnection.page) {
    if (targetElement.classList) {
      targetElement.classList.remove("connectionHover"); // no itx
      targetElement.classList.remove("connectionDeny"); // no itx

      logger.log("onMouseUp target");

      if (!state.currentDrawingConnection.connection.forbidden) {
        state.currentDrawingConnection.connect(target, connectionType);
      } else {
        state.currentDrawingConnection.connection.delete();
      }
    }
  }

  clearCurrentDrawingConnection();
}

export function onConnectionTargetMouseLeave(targetElement: HTMLDivElement) {
  lastTarget = undefined;

  if (state.currentDrawingConnection.connection) {
    if (targetElement.classList) {
      targetElement.classList.remove("connectionHover"); // no itx
      targetElement.classList.remove("connectionDeny"); // no itx
    }
    const target = state.currentDrawingConnection.connection.toDevice;
    if (target && target.className == "Channel") {
      (target as Channel).selectedForAutoconect = false;

      target.raiseOnChange();
    }
    clearCurrentDrawingConnectionTarget();
  }
}

export default function ConnectionTarget(props: {
  page: Page;
  target: ConnectableObject;
  compact: number;
  children: any;
  channelType?: ChannelType;
  connectionType: ConnectionType;
  className: string;
  style?: any;
  print?: PagePrintInfo;
  mouseUp?: () => void;
}) {
  const { page, target, compact, children, connectionType, className, style, print, mouseUp } = props;

  if (connectionType == ConnectionType.Unknown) {
    logger.error(`connectionType unknown`);
  }
  const thisElement = useRef<HTMLDivElement>(null);

  var channelStatusClass = "";

  switch (compact) {
    case 0:
      channelStatusClass = "hidden";
      return null;
      break;
    case 1:
      channelStatusClass = "compact"; // no itx
      break;
    case 2:
      channelStatusClass = "normal"; // no itx
      break;
  }

  return (
    <div
      className={`connectionTarget ${className} ${channelStatusClass} guideTarget`}
      id={target.id}
      ref={thisElement}
      style={{ ...style, pointerEvents: "auto", touchAction: "none" }}
      onMouseEnter={(ev) => {
        if (print == undefined && ev.buttons == 0) {
          highlightConnections(true, page, target);
        }
      }}
      onTouchMove={(ev: any) => {
        logger.log("ConnectionTarget onTouchMove");
        if (print == undefined && thisElement.current) {
          onConnectionTargetMouseMove(page, target, thisElement.current, connectionType);
        }
      }}
      onMouseMove={(ev: any) => {
        if (print == undefined && ev.buttons == 1 && thisElement.current) {
          onConnectionTargetMouseMove(page, target, thisElement.current, connectionType);
        }
      }}
      onMouseLeave={(ev) => {
        if (print == undefined && ev.buttons == 1 && thisElement.current) {
          onConnectionTargetMouseLeave(thisElement.current);
        }
        if (print == undefined && ev.buttons == 0) {
          dehighlightConnections(true, page, target);
        }
      }}
      onMouseUp={(ev) => {
        if (thisElement.current) {
          mouseUp && mouseUp();
          onConnectionTargetMouseUp(target, thisElement.current, connectionType);
        }
      }}
      onTouchEnd={(ev) => {
        if (thisElement.current) {
          mouseUp && mouseUp();
          onConnectionTargetMouseUp(target, thisElement.current, connectionType);
        }
      }}
    >
      {children}
    </div>
  );
}
